import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../utils/axiosConfig";
import { toast } from "react-toastify";
import { suggestEmailCorrection } from "../utils/emailTypoHelper";
import "../styles/pages/Login.css"; // Using same CSS

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uCode = searchParams.get("uCode") || searchParams.get("code");

  const [formData, setFormData] = useState({ name: "", emailOrPhone: "", phone: "", password: "" });
  const [emailSuggestion, setEmailSuggestion] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (uCode) {
      sessionStorage.setItem("pendingJoinCode", uCode.trim());
    }
  }, [uCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "emailOrPhone") {
      const suggestion = suggestEmailCorrection(value);
      setEmailSuggestion(suggestion);
    }
  };

  const applyEmailSuggestion = () => {
    if (emailSuggestion) {
      setFormData((prev) => ({ ...prev, emailOrPhone: emailSuggestion }));
      setEmailSuggestion(null);
      toast.info(`Updated email to ${emailSuggestion}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if user has an active uncorrected typo
    const directCheck = suggestEmailCorrection(formData.emailOrPhone);
    if (directCheck) {
      setEmailSuggestion(directCheck);
      toast.warning(`Did you mean "${directCheck}"? Please verify or click "Fix Email" below.`);
      return;
    }

    setLoading(true);
    try {
      const cleanEmail = formData.emailOrPhone.trim();
      await api.post("/auth/register", {
        name: formData.name.trim(),
        email: cleanEmail,
        emailOrPhone: cleanEmail,
        phone: formData.phone.trim(),
        password: formData.password,
      });
      // Store in localStorage so refresh never loses the email
      localStorage.setItem("pendingVerifyEmail", cleanEmail);
      navigate("/verify-email-otp", { state: { emailOrPhone: cleanEmail } });
    } catch (err) {
      toast.error(err.response?.data?.message || "Error registering");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="a12">
          <div className="form-img">
            <img src="/images/login-banner.jpg" alt="banner" className="c" />
            <div className="logo-wrapper"><img src="/images/logo_colored_white.png" alt="Logo" width="1299" height="192" /></div>
            <div className="overlaytext">
              <p>“This is just the beginning.”</p>
              <h4>Mohit Barthunia</h4>
              <p style={{ fontSize: "12px" }}>Founder of Fotographiya</p>
            </div>
          </div>
        </div>
        <div className="b12">
          <div className="login-form">
            <div className="mobile-only-logo"><img src="/images/logo_colored_white.png" alt="Logo" width="1299" height="192" /></div>
            <div className="loginfrom-top">
              <h2>🔰 Registration</h2>
              <span>Enter your details</span>
              <form onSubmit={handleSubmit}>
                <label style={{ width: "100%" }} htmlFor="register-name">
                  <span className="sr-only">Name</span>
                  <input id="register-name" className="input-control" type="text" name="name" placeholder="Name" value={formData.name} onChange={handleChange} required />
                </label>
                <label style={{ width: "100%" }} htmlFor="register-emailOrPhone">
                  <span className="sr-only">Email Address</span>
                  <input id="register-emailOrPhone" className="input-control" type="email" name="emailOrPhone" placeholder="Email Address" value={formData.emailOrPhone} onChange={handleChange} required />
                </label>

                {/* 💡 Friendly Real-time Typo Suggestion Box */}
                {emailSuggestion && (
                  <div
                    style={{
                      background: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      marginTop: "-8px",
                      marginBottom: "14px",
                      fontSize: "13px",
                      color: "#1e40af",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "8px",
                      boxShadow: "0 2px 4px rgba(37,99,235,0.06)",
                      animation: "fadeIn 0.2s ease-in-out"
                    }}
                  >
                    <span>
                      Did you mean <strong>{emailSuggestion}</strong>?
                    </span>
                    <button
                      type="button"
                      style={{
                        background: "#2563eb",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        padding: "4px 10px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        whiteSpace: "nowrap"
                      }}
                      onClick={applyEmailSuggestion}
                    >
                      Fix Email
                    </button>
                  </div>
                )}

                <label style={{ width: "100%" }} htmlFor="register-phone">
                  <span className="sr-only">Mobile Number</span>
                  <input id="register-phone" className="input-control" type="tel" name="phone" placeholder="Mobile Number" value={formData.phone} onChange={handleChange} required />
                </label>
                <div className="password-input-wrapper">
                  <label style={{ width: "100%", display: "block" }} htmlFor="register-password">
                    <span className="sr-only">Password</span>
                    <input
                      id="register-password"
                      className="input-control"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                    />
                  </label>
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
                <button className="btn btn-primary-gradient login-btn" type="submit" disabled={loading}>
                  {loading ? "Registering..." : "Register"}
                </button>
              </form>

              <div className="auth-switch-card">
                <div className="auth-switch-info">
                  <span>Already a user?</span>
                </div>
                <Link
                  to={uCode ? `/login?uCode=${uCode}` : "/login"}
                  className="btn-auth-switch"
                  id="register-login-btn"
                >
                  <span>Login</span>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Register;